import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getHotelOfferUrl } from 'lib/offer/offerPageURL'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import ExperimentalSearchTileFeaturesList, { MAX_INCLUSIONS_TO_SHOW } from '../shared/ExperimentalSearchTileFeaturesList'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import useLoyaltyBenefitInclusions from 'loyalty/hooks/useLoyaltyBenefitInclusions'
import ExperiencesPassportOfferSummary from 'components/Experiences/ExperiencesPassport/ExperiencesPassportOfferSummary'
import { resolveMappedSearchFilterInclusions, shouldShowCompactMappedInclusionsOverflow, useSearchFilterCategoryInclusions } from '../shared/useSearchFilterCategoryInclusions'
import { type SearchTileCancellationInclusionKind, useSearchTileCancellationInclusion } from '../shared/SearchTileFreeCancellationInclusion'
import { getCompactInclusionListLimit } from './accommodationSearchTileView'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
  maxInclusionsToShow?: number
  hideLoyaltyInclusions?: boolean
  mapToSearchFilterCategories?: boolean
  cancellationInclusionKind?: SearchTileCancellationInclusionKind
}

function ExperimentalBedbankLTSSearchTileFeatures({
  offer,
  filters,
  maxInclusionsToShow = MAX_INCLUSIONS_TO_SHOW,
  hideLoyaltyInclusions = false,
  mapToSearchFilterCategories = false,
  cancellationInclusionKind,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const loyaltyBenefitCount = useLoyaltyBenefitInclusions(offer).length
  const loyaltyCount = hideLoyaltyInclusions ? 0 : loyaltyBenefitCount
  const showLoyaltyInline = !hideLoyaltyInclusions && ((offer.inclusions?.length ?? 0) + loyaltyBenefitCount) <= maxInclusionsToShow
  const sourceInclusions = offer.inclusions ?? EmptyArray
  const cancellationInclusion = useSearchTileCancellationInclusion(cancellationInclusionKind)
  const mappedSearchFilterInclusions = useSearchFilterCategoryInclusions(sourceInclusions, maxInclusionsToShow)
  const displayedInclusions = useMemo(() => {
    const mapped = resolveMappedSearchFilterInclusions(
      mapToSearchFilterCategories,
      mappedSearchFilterInclusions.inclusions,
      sourceInclusions,
    )
    if (!mapToSearchFilterCategories || !cancellationInclusion) return mapped
    return [cancellationInclusion, ...mapped]
  }, [cancellationInclusion, mapToSearchFilterCategories, mappedSearchFilterInclusions.inclusions, sourceInclusions])
  const inclusionOverflowLabel = shouldShowCompactMappedInclusionsOverflow(
    mapToSearchFilterCategories,
    sourceInclusions.length,
    mappedSearchFilterInclusions.inclusions.length,
  ) ? mappedSearchFilterInclusions.showMoreLabel : undefined

  const openAllInclusionsModal = useCallback(() => {
    showModal(<Modal
      title={offer.property.name}
      primaryActionTo={getHotelOfferUrl(offer.id, offer.type)}
      primaryActionText={intl.formatMessage({
        id: 'o5cLrPpVrrMf845Pglrx',
        defaultMessage: 'View offer',
      })}
    >
      <Group direction="vertical" gap={4}>
        {!!offer.inclusions?.length && <OfferInclusionsList inclusions={offer.inclusions} />}
        {!hideLoyaltyInclusions && <LoyaltyInclusions offer={offer} variant="offer-page" />}
      </Group>
    </Modal>)
  }, [showModal, offer, intl, hideLoyaltyInclusions])

  if (!offer.inclusions?.length && !loyaltyCount && !cancellationInclusionKind) {
    return mapToSearchFilterCategories ? null : <ExperiencesPassportOfferSummary offer={offer} filters={filters} variant="search-tile" />
  }

  return (
    <Group direction="vertical" gap={4}>
      {!mapToSearchFilterCategories && <ExperiencesPassportOfferSummary offer={offer} filters={filters} variant="search-tile" />}
      {(!!displayedInclusions.length || !!inclusionOverflowLabel) && <ExperimentalSearchTileFeaturesList
        features={displayedInclusions}
        featuresType="inclusions"
        onMoreClick={openAllInclusionsModal}
        additionalHiddenCount={mapToSearchFilterCategories || showLoyaltyInline ? 0 : loyaltyCount}
        maxInclusionsToShow={getCompactInclusionListLimit(
          maxInclusionsToShow,
          mapToSearchFilterCategories && !!cancellationInclusion,
        )}
        overflowLabel={inclusionOverflowLabel}
        inclusionSize={mapToSearchFilterCategories ? 'small' : undefined}
      />}
      {!mapToSearchFilterCategories && showLoyaltyInline && <LoyaltyInclusions offer={offer} variant="search-tile" />}
    </Group>
  )
}

export default ExperimentalBedbankLTSSearchTileFeatures
